import { ArrowLeft, Ban, Download, RotateCcw } from "lucide-react";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader } from "@/components/common/loader";
import { openDocument } from "@/features/documents/api";
import { downloadSignedPdf } from "@/features/esignatures/api";
import { ESignatureStatusBadge } from "@/features/esignatures/components/esignature-status-badge";
import { ResendDialog } from "@/features/esignatures/components/resend-dialog";
import { RevokeDialog } from "@/features/esignatures/components/revoke-dialog";
import { useESignature } from "@/features/esignatures/hooks/use-esignature";
import { ACTIONABLE_STATUSES } from "@/features/esignatures/types";
import { useActiveClient } from "@/layouts/client-layout";

function formatDateTime(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export default function ESignatureRequestDetailPage() {
  const { client } = useActiveClient();
  const { requestId } = useParams<{ requestId: string }>();
  const { request, loading, error, refetch } = useESignature(requestId ?? "");
  const [resendOpen, setResendOpen] = useState(false);
  const [revokeOpen, setRevokeOpen] = useState(false);

  if (loading) return <Loader className="mt-10" />;
  if (error || !request) {
    return (
      <div className="flex flex-col items-center gap-4 py-16 text-center">
        <p className="text-muted-foreground text-sm">{error ?? "Request not found."}</p>
        <Button asChild variant="outline">
          <Link to={`/client/${client.id}/esignatures`}>Back to E-Signatures</Link>
        </Button>
      </div>
    );
  }

  const canAct = ACTIONABLE_STATUSES.includes(request.status);

  return (
    <div className="flex flex-col gap-6">
      <div>
        <Link
          to={`/client/${client.id}/esignatures`}
          className="text-muted-foreground hover:text-primary mb-2 inline-flex items-center gap-1 text-sm"
        >
          <ArrowLeft className="size-4" /> E-Signatures
        </Link>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-foreground text-2xl font-bold">{request.documentName}</h1>
          <div className="flex items-center gap-2">
            {request.status === "COMPLETED" && (
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  downloadSignedPdf(request.id, `${request.documentName} (signed).pdf`).catch(
                    () => toast.error("Couldn't download the signed document."),
                  )
                }
              >
                <Download /> Signed PDF
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => openDocument(request.document).catch(() => toast.error("Couldn't open the document."))}>
              <Download /> Original
            </Button>
            {canAct && (
              <>
                <Button variant="outline" size="sm" onClick={() => setResendOpen(true)}>
                  <RotateCcw /> Resend
                </Button>
                <Button variant="outline" size="sm" onClick={() => setRevokeOpen(true)}>
                  <Ban /> Revoke
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Request header */}
      <Card className="grid grid-cols-2 gap-4 p-6 sm:grid-cols-4">
        <Field label="Status">
          <ESignatureStatusBadge status={request.status} label={request.statusDisplay} />
        </Field>
        <Field label="Sent by">{request.sentByName || "—"}</Field>
        <Field label="Sent at">{formatDateTime(request.sentAt)}</Field>
        <Field label="Sign-by date">
          {request.signByDate ? formatDateTime(`${request.signByDate}T00:00:00`) : "—"}
        </Field>
        {request.completedAt && (
          <Field label="Completed at">{formatDateTime(request.completedAt)}</Field>
        )}
        {request.voidedAt && (
          <Field label="Revoked at">{formatDateTime(request.voidedAt)}</Field>
        )}
      </Card>

      {/* Recipients */}
      <Card className="gap-0 overflow-hidden p-0">
        <div className="border-b px-6 py-4">
          <h2 className="text-foreground font-semibold">Recipients</h2>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Viewed</TableHead>
              <TableHead>Signed</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {request.recipients.map((r) => (
              <TableRow key={r.id}>
                <TableCell className="font-medium">{r.name}</TableCell>
                <TableCell className="text-muted-foreground text-sm">{r.email}</TableCell>
                <TableCell className="text-muted-foreground text-sm">{r.roleDisplay}</TableCell>
                <TableCell>
                  <ESignatureStatusBadge
                    status={r.status}
                    label={r.statusDisplay}
                    title={r.bounceReason || r.statusDisplay}
                  />
                </TableCell>
                <TableCell className="text-muted-foreground text-sm">
                  {formatDateTime(r.viewedAt)}
                </TableCell>
                <TableCell className="text-muted-foreground text-sm">
                  {formatDateTime(r.signedAt)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Audit trail */}
      <Card className="gap-4 p-6">
        <h2 className="text-foreground font-semibold">Audit trail</h2>
        {request.events.length === 0 ? (
          <p className="text-muted-foreground text-sm">No events recorded.</p>
        ) : (
          <ol className="border-border relative ml-3 space-y-5 border-l-2">
            {request.events.map((e) => (
              <li key={e.id} className="relative pl-6">
                <span className="bg-primary ring-card absolute top-1.5 -left-[7px] size-3 rounded-full ring-4" />
                <div className="flex flex-wrap items-center gap-x-2 text-sm">
                  <span className="text-foreground font-semibold">{e.eventTypeDisplay}</span>
                  <span className="text-muted-foreground text-xs">
                    {formatDateTime(e.createdAt)}
                  </span>
                  {e.actorName && (
                    <span className="text-muted-foreground text-xs">· {e.actorName}</span>
                  )}
                </div>
                {e.detail && <p className="text-muted-foreground text-sm">{e.detail}</p>}
              </li>
            ))}
          </ol>
        )}
      </Card>

      <ResendDialog
        request={resendOpen ? request : null}
        onOpenChange={setResendOpen}
        onResent={refetch}
      />
      <RevokeDialog
        request={revokeOpen ? request : null}
        onOpenChange={setRevokeOpen}
        onRevoked={refetch}
      />
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
        {label}
      </div>
      <div className="text-foreground mt-1 text-sm">{children}</div>
    </div>
  );
}
